import {PAYMENT_KEYS} from "@/api/paymentApi";
import {USER_KEYS} from "@/api/userApi";
import {useFetchUser} from "@/hooks/useFetchUser";
import {client} from "@/httpClient";
import paymentErrorsDe from "@/locale/ger/payment_errors.json";
import type {TInvoice, TPackageName, TPackagePaymentPeriodType} from "@/store/types";
import useUserStore from "@/store/user";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useCallback} from "react";
import {useTranslation} from "react-i18next";

const usePayment = () => {
  const {t} = useTranslation();
  const {refetch} = useFetchUser();
  const queryClient = useQueryClient();
  const user = useUserStore(state => state.user);

  const getTranslatedErrorMessage = useCallback((invoice: TInvoice) => {
    if (!invoice.failure_code) return "";

    if (Object.keys(paymentErrorsDe).includes(invoice.failure_code)) return t(`payment_errors:${invoice.failure_code}`);

    return invoice.failure_message;
  }, []);

  const {mutateAsync: purchasePlan} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageListSubscribe,
    mutationFn: ({
      package_type,
      packageId,
      packageType,
    }: {
      package_type: TPackagePaymentPeriodType;
      packageId: number;
      packageType?: TPackageName;
      success_url?: string;
      cancel_url?: string;
    }) =>
      client.post(`/packages/${packageId}/subscribe/`, {
        package_type,
        success_url:
          window.location.origin + "/welcome/" + (typeof packageType !== "undefined" ? `?plan=${packageType}` : ""),
        cancel_url: window.location.href,
      }),
    onSuccess: response => {
      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }
    },
  });

  const {mutateAsync: upgradePlan, isPending: upgradePlanIsLoading} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageSubscriptionUpgrade,
    mutationFn: ({
      package_type,
      package_id,
      subscriptionId,
    }: {
      package_type: TPackagePaymentPeriodType;
      package_id: number;
      subscriptionId: number;
    }) => client.post(`/package_subscriptions/${subscriptionId}/upgrade/`, {package_type, package_id}),
    onSuccess: async () => await refetch(),
  });

  const {mutateAsync: payInvoices, isPending: invoicePayIsLoading} = useMutation({
    mutationKey: PAYMENT_KEYS.mInvoiceBatchPayment,
    mutationFn: ({invoiceIds}: {invoiceIds: number[]}) =>
      client.post(PAYMENT_KEYS.mInvoiceBatchPayment[0], {invoice_ids: invoiceIds}),
    onSuccess: response => {
      const userId = Number(useUserStore.getState().user.id);

      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }

      setTimeout(() => {
        queryClient.invalidateQueries({queryKey: USER_KEYS.qUser(userId)});
      }, 2000);
    },
  });

  const {mutateAsync: attachPaymentMethod, isPending: attachLoading} = useMutation({
    mutationKey: USER_KEYS.mUserPaymentMethodAttach(user.id!),
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) =>
      client.post(USER_KEYS.mUserPaymentMethodAttach(user.id!)[0], {payment_method_id: paymentMethodId}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: USER_KEYS.qUserPaymentMethods(user.id!)}),
  });

  const {mutateAsync: detachPaymentMethod, isPending: detachLoading} = useMutation({
    mutationKey: USER_KEYS.mUserPaymentMethodDetach(user.id!),
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) =>
      client.post(USER_KEYS.mUserPaymentMethodDetach(user.id!)[0], {payment_method_id: paymentMethodId}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: USER_KEYS.qUserPaymentMethods(user.id!)}),
  });

  const {mutateAsync: setDefaultPaymentMethod, isPending: setDefaultLoading} = useMutation({
    mutationKey: USER_KEYS.mUserPaymentMethodDefault(user.id!),
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) =>
      client.post(USER_KEYS.mUserPaymentMethodDefault(user.id!)[0], {payment_method_id: paymentMethodId}),
    onSuccess: () => queryClient.invalidateQueries({queryKey: USER_KEYS.qUserPaymentMethods(user.id!)}),
  });

  return {
    purchasePlan,
    upgradePlan,
    upgradePlanIsLoading,
    payInvoices,
    invoicePayIsLoading,
    attachPaymentMethod,
    attachLoading,
    detachPaymentMethod,
    detachLoading,
    setDefaultPaymentMethod,
    setDefaultLoading,
    getTranslatedErrorMessage,
  };
};

export {usePayment};
